import type { FilterGroupData } from "./advanced-filter-builder/constants";

/** Form + API section for one Miqaat RSVP audience arm. */
export type MiqaatRsvpFilterSection = {
  include: boolean;
  /** null / empty group = all of source when include is true. */
  filter: FilterGroupData | null;
};

/**
 * Dual-audience filterCriteria envelope for MIQAAT_RSVP broadcasts.
 * Matches API `parseMiqaatRsvpFilterCriteria` shape (`itsDirectory` / `muttavatteen`).
 */
export type MiqaatRsvpFilterEnvelope = {
  itsDirectory: MiqaatRsvpFilterSection;
  muttavatteen: MiqaatRsvpFilterSection;
};

export const createEmptyFilterGroup = (id = "root"): FilterGroupData => ({
  id,
  logic: "AND",
  rules: [],
  groups: [],
});

/** New MIQAAT_RSVP form default: both sources included, no rules. */
export const createDefaultMiqaatRsvpFilterEnvelope = (): MiqaatRsvpFilterEnvelope => ({
  itsDirectory: { include: true, filter: createEmptyFilterGroup("its-root") },
  muttavatteen: { include: true, filter: createEmptyFilterGroup("muttavatteen-root") },
});

export const isMiqaatRsvpFilterEnvelope = (value: unknown): value is MiqaatRsvpFilterEnvelope => {
  if (value == null || typeof value !== "object" || Array.isArray(value)) {
    return false;
  }
  const record = value as Record<string, unknown>;
  return "itsDirectory" in record || "muttavatteen" in record;
};

export const filterGroupHasConstraints = (filter: FilterGroupData | null | undefined): boolean => {
  if (!filter) return false;
  return (filter.rules?.length ?? 0) > 0 || (filter.groups?.length ?? 0) > 0;
};

/** True when at least one audience section is included (preview/send may proceed). */
export const hasMiqaatRsvpAudienceIncluded = (envelope: MiqaatRsvpFilterEnvelope): boolean =>
  Boolean(envelope.itsDirectory?.include || envelope.muttavatteen?.include);

const normalizeSection = (
  raw: unknown,
  defaultInclude: boolean,
  emptyGroupId: string
): MiqaatRsvpFilterSection => {
  if (raw == null || typeof raw !== "object" || Array.isArray(raw)) {
    return { include: defaultInclude, filter: createEmptyFilterGroup(emptyGroupId) };
  }
  const section = raw as Record<string, unknown>;
  const include = typeof section.include === "boolean" ? section.include : defaultInclude;
  const filterRaw = section.filter;
  if (filterRaw == null) {
    return { include, filter: createEmptyFilterGroup(emptyGroupId) };
  }
  if (typeof filterRaw === "object" && !Array.isArray(filterRaw)) {
    const group = filterRaw as FilterGroupData;
    return {
      include,
      filter: {
        id: typeof group.id === "string" ? group.id : emptyGroupId,
        logic: group.logic === "OR" ? "OR" : "AND",
        rules: Array.isArray(group.rules) ? group.rules : [],
        groups: Array.isArray(group.groups) ? group.groups : [],
      },
    };
  }
  return { include, filter: createEmptyFilterGroup(emptyGroupId) };
};

/** Coerce form/API values into a full envelope for the dual UI. */
export const parseMiqaatRsvpFilterEnvelopeForForm = (value: unknown): MiqaatRsvpFilterEnvelope => {
  if (isMiqaatRsvpFilterEnvelope(value)) {
    return {
      itsDirectory: normalizeSection(value.itsDirectory, false, "its-root"),
      muttavatteen: normalizeSection(value.muttavatteen, false, "muttavatteen-root"),
    };
  }
  return createDefaultMiqaatRsvpFilterEnvelope();
};

/**
 * API payload for MIQAAT_RSVP: always the dual envelope.
 * Empty rule trees become `filter: null` (all of source when include is true).
 */
export const transformMiqaatRsvpFilterCriteria = (value: unknown): MiqaatRsvpFilterEnvelope => {
  const form = parseMiqaatRsvpFilterEnvelopeForForm(
    value ?? createDefaultMiqaatRsvpFilterEnvelope()
  );

  const toApiSection = (section: MiqaatRsvpFilterSection): MiqaatRsvpFilterSection => ({
    include: Boolean(section.include),
    filter: filterGroupHasConstraints(section.filter) ? section.filter : null,
  });

  return {
    itsDirectory: toApiSection(form.itsDirectory),
    muttavatteen: toApiSection(form.muttavatteen),
  };
};
